import { Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/Info';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import useTapOrCoarsePointerTooltip from '../../hooks/useTapOrCoarsePointerTooltip';
import MarkdownToHtml from '../MarkdownToHtml';

const classes = {
  success: 'text-green-600',
  successBlue: 'text-blue-500',
  danger: 'text-red-600',
  default: 'text-gray-600',
  warning: 'text-orange-600',
  theme: 'text-theme',
  themeWithBg: 'text-theme bg-theme bg-opacity-25 px-2 py-1 font-bold text-sm rounded-lg',
};

const getClasses = (level, italic, inline, capitalize, className) =>
  clsx(
    classes[level],
    italic && 'italic',
    'font-normal',
    inline && 'inline',
    capitalize && 'capitalize',
    className,
  );

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.18)',
    fontSize: 11,
    maxWidth: '350px',
  },
  arrow: {
    color: theme.palette.common.white,
    // The MUI v4 arrow is a rotated square (::before); give it the same shadow as the
    // tooltip box so the arrow looks connected instead of flat. The half tucked behind the
    // tooltip is covered, so only the outer edge casts a shadow.
    '&::before': {
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.18)',
    },
  },
}))(Tooltip);

const Label = ({
  text,
  icon,
  level,
  italic,
  inline,
  doShow,
  descriptionOnHover,
  tooltipPlacement,
  capitalize,
  className,
  showInfoIndicator,
}) => {
  const { tapMode, tooltipProps, triggerBindings } = useTapOrCoarsePointerTooltip();

  if (!doShow) return <></>;

  const showIndicator = !!showInfoIndicator && !!descriptionOnHover;
  const showUnderline = showInfoIndicator === true;
  const textSpan = showIndicator ? (
    <span className="inline-flex items-baseline">
      <span
        className={clsx(
          showUnderline && 'border-b border-dotted border-gray-400 pb-px hover:border-gray-500',
        )}
      >
        {text}
      </span>
      <sup className="ml-0.5 leading-none">
        <InfoIcon
          className={clsx('block', showIndicator && classes[level])}
          style={{ fontSize: '0.6875rem' }}
          aria-hidden="true"
        />
      </sup>
    </span>
  ) : (
    <span>{text}</span>
  );

  const renderLabelContent = (
    <div
      className={clsx(
        getClasses(showIndicator ? null : level, italic, inline, capitalize, className),
        {
          'leading-4 flex': icon,
          'gap-1': text && icon,
          'cursor-help': showIndicator && !tapMode,
        },
      )}
    >
      {icon &&
        React.cloneElement(icon, {
          className: clsx(
            'inline-block',
            icon.props && icon.props.className,
            showIndicator && classes[level],
          ),
        })}
      {textSpan}
    </div>
  );

  if (descriptionOnHover) {
    return (
      <LightTooltip
        title={
          typeof descriptionOnHover === 'string' ? (
            <MarkdownToHtml
              content={descriptionOnHover}
              className="text-xs font-normal text-black"
            />
          ) : (
            descriptionOnHover
          )
        }
        placement={tooltipPlacement}
        arrow
        {...tooltipProps}
      >
        {tapMode ? (
          <div className="inline-block cursor-pointer" {...triggerBindings}>
            {renderLabelContent}
          </div>
        ) : (
          renderLabelContent
        )}
      </LightTooltip>
    );
  }
  return renderLabelContent;
};

Label.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  icon: PropTypes.node,
  level: PropTypes.string,
  italic: PropTypes.bool,
  inline: PropTypes.bool,
  doShow: PropTypes.bool,
  capitalize: PropTypes.bool,
  descriptionOnHover: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  tooltipPlacement: PropTypes.string,
  className: PropTypes.string,
  showInfoIndicator: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

Label.defaultProps = {
  text: '',
  level: 'default',
  italic: false,
  inline: false,
  icon: undefined,
  doShow: true,
  capitalize: false,
  descriptionOnHover: undefined,
  tooltipPlacement: undefined,
  className: '',
  showInfoIndicator: false,
};

export default Label;
